import React, { useRef, useState } from 'react';
import { useTriggerOnClickOutsideElement } from '@lmt/unicorn-shared/app/helpers/useTriggerOnClickOutsideElement';
import { OptionList } from '@lmt/unicorn-shared/app/components/common/ui-kit/OptionList';
import { ReactComponent as MenuButtonIcon } from '@lmt/unicorn-shared/app/icons/nav-menu-arrow-down.svg';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { getState, useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useNavigate } from 'react-router-dom';
import styles from './NabTabsMenuButton.module.scss';
import { useTranslation } from 'react-i18next';
import { useHotkeys } from 'react-hotkeys-hook';
import { IconButton } from '../../common/ui-kit/IconButton';
import { useEventCallback } from '@mui/material';
import { useTabId } from '../../keep-alive/TabContext';

interface NavMenuOption {
  displayValue: string;
  value: string;
  onClick?: () => void;
  disabled?: boolean;
}

export const NavTabsMenuButton: React.FC = () => {
  const dispatch = useUniDispatch();
  const navigate = useNavigate();
  const i18nextGlobal = useTranslation();
  const tabId = useTabId();
  const onCloseTabs = useEventCallback(() => {
    const tabs = getState().navigation.tabs;
    dispatch(NavigationActions.tabsRemovedAll());
    const { tabStateChangeSubscribers } = getState().navigation;
    let hasChanges = false;
    for (const tab of tabs) {
      if (tab.hasUnsavedChanges) {
        hasChanges = true;
        continue;
      }
      tabStateChangeSubscribers.forEach((subscriber) => {
        if (tab.id) {
          subscriber?.(tab.id, 'removed');
        }
      });
    }
    if (hasChanges) {
      const openedTabs = getState().navigation.tabs;
      if (openedTabs.find((x) => x.id === tabId)) {
        // current tab is one of the tabs, that has unsaved changed and therefore is left opened
        // do nothing.
        return;
      }
    }
    const defaultTabs = getState().navigation.defaultTabs;
    const patientTab = defaultTabs[0];
    navigate(
      { pathname: patientTab.path, search: patientTab.search },
      { replace: true },
    );
  });
  useHotkeys('ctrl+alt+w', onCloseTabs);
  const options: NavMenuOption[] = [
    {
      value: 'closeAllTabs',
      displayValue: i18nextGlobal.t('tabs.menu.close_all_tabs'),
      onClick: () => onCloseTabs(),
    },
  ];

  const [expanded, setExpanded] = useState<boolean>(false);
  const optionListRef = useRef<HTMLDivElement>(null);
  const hide = useEventCallback(() => setExpanded(false));
  useTriggerOnClickOutsideElement(optionListRef, hide, expanded);

  return (
    <div data-test-id={'navigation_menu_button'}>
      <IconButton
        className={styles.icon}
        onClick={() => setExpanded(!expanded)}
        iconComponent={<MenuButtonIcon className={styles.formButtonIcon} />}
      />
      <OptionList<NavMenuOption>
        visible={expanded}
        options={options}
        onClick={(option) => {
          option.onClick?.();
          setExpanded(false);
        }}
        labelField="displayValue"
        elementRef={optionListRef}
        className={styles.optionListContainer}
      />
    </div>
  );
};
